import React, { useState } from 'react';
import { geocodeApi } from '../lib/api';
import { geocodeError } from '../lib/geocode';
import { useTheme } from '../contexts/ThemeContext';

/**
 * "Find the site": a text box with a Search button (and Enter) that asks the geocoder and
 * lists the matches. Choosing one calls onSelect(result) with { label, lon, lat, bbox, source, approximate }
 * (approximate: a street-level match, the house number was not found).
 * Used by the Georeference dialog and the Map tab's Find address popover.
 */
export default function AddressSearch({ onSelect, autoFocus = false, placeholder = 'Address or place, e.g. 2121 Geoscience Dr, Austin' }) {
  const { C } = useTheme();
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null); // null until the first search
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [chosen, setChosen] = useState(null);

  const search = async () => {
    const text = q.trim();
    if (busy) return;
    setChosen(null);
    if (text.length < 3) { setResults(null); setError('Type at least 3 characters'); return; }
    setBusy(true);
    setError(null);
    try {
      setResults(await geocodeApi.search(text));
    } catch (e) {
      setResults(null);
      setError(geocodeError(e));
    } finally {
      setBusy(false);
    }
  };

  const choose = (r, i) => { setChosen(i); onSelect?.(r); };

  const field = { flex: 1, minWidth: 0, fontSize: 12, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', boxSizing: 'border-box' };
  return (
    <div data-testid="address-search">
      <div style={{ display: 'flex', gap: 6 }}>
        <input type="text" aria-label="Address or place" value={q} autoFocus={autoFocus} placeholder={placeholder}
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); search(); } }} style={field} />
        <button type="button" onClick={search} disabled={busy}
          style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, cursor: busy ? 'wait' : 'pointer', background: C.bg4, color: C.text1, border: `1px solid ${C.border}` }}>
          {busy ? 'Searching…' : 'Search'}
        </button>
      </div>
      {error && <div role="alert" style={{ fontSize: 11, color: '#f87171', marginTop: 4 }}>{error}</div>}
      {results && results.length === 0 && <div style={{ fontSize: 11, color: C.text3, marginTop: 4 }}>No results</div>}
      {results && results.length > 0 && (
        <ul role="listbox" aria-label="Address results" style={{ listStyle: 'none', margin: '4px 0 0', padding: 0, border: `1px solid ${C.border}`, borderRadius: 6, overflow: 'hidden' }}>
          {results.map((r, i) => (
            <li key={`${r.lon},${r.lat},${i}`} role="option" aria-selected={chosen === i}>
              <button type="button" onClick={() => choose(r, i)}
                style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 11, padding: '5px 8px', cursor: 'pointer', border: 'none', borderTop: i ? `1px solid ${C.border}` : 'none',
                  background: chosen === i ? 'rgba(0,212,255,0.12)' : C.bg3, color: C.text1 }}>
                {r.label}
                {r.approximate && <span style={{ fontSize: 10, color: C.text2 }}>{' · street match, house number not found'}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
